import type {
  PolicySnapshot,
  ProductComparisonMatch,
  ProductRider,
  StrategyComparison,
} from '@fig/shared';
import { ChevronDown, ChevronRight, Sparkles } from 'lucide-react';
import { Component, useState } from 'react';
import type { ErrorInfo, ReactNode } from 'react';

import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { TruncatedTooltip } from '@/components/ui/truncated-tooltip';
import { useProductDiff } from '@/hooks/api/useProductDiff';
import { normalizeProductTypeLabel } from '@/lib/product-types';
import { bestPremiumBonus, issueAgeBonusLabel, premiumBandLabel } from '@/lib/rate-bands';
import { cn } from '@/lib/utils';

class RowErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state: { error: Error | null } = { error: null };
  static getDerivedStateFromError(error: Error) {
    return { error };
  }
  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('ComparisonTable row render error:', error, info);
  }
  render() {
    if (this.state.error) {
      return (
        <tr>
          <td colSpan={99} className="px-4 py-2 text-xs text-destructive">
            Row render error: {this.state.error.message}
          </td>
        </tr>
      );
    }
    return this.props.children;
  }
}

interface ComparisonTableProps {
  opportunityId: string;
  currentPolicy: PolicySnapshot;
  matches: ProductComparisonMatch[];
  selectable?: boolean;
  checkedIds?: Set<string>;
  onToggleCheck?: (match: ProductComparisonMatch) => void;
  onDismissProduct?: (match: ProductComparisonMatch) => void;
}

/** Extract S&P 500 Annual PTP rate from a match */
function getSP500RateFromMatch(m: ProductComparisonMatch): StrategyComparison | null {
  for (const s of m.rateComparison?.strategyComparisons ?? []) {
    const upper = s.accountName.toUpperCase();
    const source = (s.accountSourceCode ?? '').toUpperCase();
    const isSP500 =
      upper.includes('S&P') ||
      upper.includes('SP500') ||
      source.includes('SP500') ||
      source.includes('S&P');
    const isPTP = upper.includes('POINT') || upper.includes('PTP') || upper.includes('ANNUAL');
    if (isSP500 && isPTP) {
      return s;
    }
  }
  return null;
}

const COLUMNS = ['Product', 'Type', 'Surrender', 'Best Rate', 'Rating', 'Match Score'] as const;

function getCurrentValue(col: (typeof COLUMNS)[number], policy: PolicySnapshot): React.ReactNode {
  switch (col) {
    case 'Product':
      return policy.productName ?? '—';
    case 'Type':
      return policy.productType;
    case 'Surrender':
      return policy.yearsUntilSurrender != null
        ? `${policy.yearsUntilSurrender.toFixed(1)}yr remaining`
        : '—';
    case 'Best Rate':
      return policy.currentCapRate != null ? `${policy.currentCapRate}% cap` : '—';
    case 'Rating':
      return policy.carrierName;
    default:
      return '—';
  }
}

function getMatchValue(col: (typeof COLUMNS)[number], m: ProductComparisonMatch): React.ReactNode {
  switch (col) {
    case 'Product':
      return m.annuityName;
    case 'Type':
      return normalizeProductTypeLabel(m.annuityType);
    case 'Surrender':
      return m.surrenderPeriodYears != null ? `${m.surrenderPeriodYears}yr` : '—';
    case 'Best Rate': {
      const sp500 = getSP500RateFromMatch(m);
      if (sp500) {
        const bandLabel = premiumBandLabel(m.accounts);
        return (
          <div className="flex flex-col items-end">
            <span className="font-medium">{formatRate(sp500.newRate, sp500.rateType)}</span>
            <span className="text-[10px] text-muted-foreground">
              {formatRateLabel(sp500.rateType)}
              {bandLabel ? ` · ${bandLabel}` : ''}
            </span>
          </div>
        );
      }
      // Fixed Annuity Products have no S&P 500 crediting strategy — fall back to
      // the guaranteed yield to surrender ("GTD Yield Surrender" in ARW).
      if (
        normalizeProductTypeLabel(m.annuityType) === 'Fixed Annuity Products' &&
        m.guaranteedYieldToSurrender != null
      ) {
        return (
          <div className="flex flex-col items-end">
            <span className="font-medium">{m.guaranteedYieldToSurrender.toFixed(2)}%</span>
            <span className="text-[10px] text-muted-foreground">GTD Yield Surrender</span>
          </div>
        );
      }
      return '—';
    }
    case 'Rating':
      return m.bestRating ?? '—';
    case 'Match Score':
      return (
        <>
          <span className="font-semibold">{m.recommendationScore}</span>
          <span className="text-muted-foreground">/100</span>
        </>
      );
  }
}

const RATE_TYPE_LABEL: Record<string, string> = {
  cap: 'Cap',
  participation: 'Participation',
  fixed: 'Fixed',
  spread: 'Spread',
};

function formatRate(rate: number, rateType: string): string {
  if (rateType === 'participation') return `${rate}% participation`;
  if (rateType === 'fixed') return `${rate.toFixed(2)}% fixed`;
  if (rateType === 'spread') return `${rate.toFixed(2)}% spread`;
  return `${rate.toFixed(2)}% cap`;
}

function formatRateLabel(rateType: string): string {
  return `S&P 500 ${RATE_TYPE_LABEL[rateType] ?? rateType}`;
}

// ---------------------------------------------------------------------------
// Expanded detail panel sub-components
// ---------------------------------------------------------------------------

function DetailLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground mb-1.5">
      {children}
    </p>
  );
}

function StrategyList({ strategies }: { strategies: StrategyComparison[] }) {
  if (strategies.length === 0) return null;

  // Sort S&P 500 PTP to the top
  const sorted = [...strategies].sort((a, b) => {
    const aUpper = a.accountName.toUpperCase();
    const bUpper = b.accountName.toUpperCase();
    const aIsSP =
      (aUpper.includes('S&P') || aUpper.includes('SP500')) &&
      (aUpper.includes('POINT') || aUpper.includes('PTP') || aUpper.includes('ANNUAL'));
    const bIsSP =
      (bUpper.includes('S&P') || bUpper.includes('SP500')) &&
      (bUpper.includes('POINT') || bUpper.includes('PTP') || bUpper.includes('ANNUAL'));
    if (aIsSP && !bIsSP) return -1;
    if (!aIsSP && bIsSP) return 1;
    return 0;
  });

  return (
    <div>
      <DetailLabel>Crediting Strategies</DetailLabel>
      <div className="space-y-1">
        {sorted.map((s: StrategyComparison, i: number) => {
          const improvement = s.absoluteImprovement;
          const hasImprovement = improvement !== null && improvement > 0;
          const upper = s.accountName.toUpperCase();
          const isSP500 =
            (upper.includes('S&P') || upper.includes('SP500')) &&
            (upper.includes('POINT') || upper.includes('PTP') || upper.includes('ANNUAL'));
          return (
            <div
              key={i}
              className={cn(
                'flex items-center justify-between text-xs',
                isSP500 && 'bg-primary/5 rounded px-1.5 py-0.5 -mx-1.5'
              )}
            >
              <span className="mr-3 min-w-0 flex-1">
                <TruncatedTooltip
                  text={s.accountName}
                  className={cn(isSP500 ? 'font-medium' : 'text-muted-foreground')}
                >
                  <>
                    {s.accountName}
                    {isSP500 && <span className="ml-1 text-[10px] text-primary">(Primary)</span>}
                  </>
                </TruncatedTooltip>
              </span>
              <div className="flex items-center gap-2 shrink-0">
                <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                  {RATE_TYPE_LABEL[s.rateType] ?? s.rateType}
                </Badge>
                <span className="font-medium">{s.newRate.toFixed(2)}%</span>
                {hasImprovement && (
                  <span className="text-success text-[10px]">+{improvement.toFixed(2)}</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function FeatureBadges({ match }: { match: ProductComparisonMatch }) {
  const hasSomething =
    match.mva || match.rop || match.withdrawalAllowed || match.withdrawalInterest;
  if (!hasSomething && !(match.accountCount ?? 0)) return null;
  return (
    <div>
      <DetailLabel>Features</DetailLabel>
      <div className="flex flex-wrap gap-1">
        {match.mva && (
          <Badge variant="outline" className="text-[10px] px-1.5 py-0">
            MVA
          </Badge>
        )}
        {match.rop && (
          <Badge variant="outline" className="text-[10px] px-1.5 py-0">
            ROP
          </Badge>
        )}
        {match.withdrawalAllowed &&
          match.withdrawalAllowed !== 'No' &&
          match.withdrawalAllowed !== '' &&
          match.withdrawalAllowed !== '0%' && (
            <Badge variant="outline" className="text-[10px] px-1.5 py-0">
              Free W/D{match.withdrawalAllowed.includes('%') ? `: ${match.withdrawalAllowed}` : ''}
            </Badge>
          )}
        {match.withdrawalInterest && (
          <Badge variant="outline" className="text-[10px] px-1.5 py-0">
            Interest W/D
          </Badge>
        )}
        {(match.accountCount ?? 0) > 0 && (
          <Badge variant="outline" className="text-[10px] px-1.5 py-0">
            {match.accountCount} strategies
          </Badge>
        )}
      </div>
    </div>
  );
}

function CommissionBonusSection({ match }: { match: ProductComparisonMatch }) {
  const commissions = match.commissions ?? [];
  const bonuses = match.premiumBonuses ?? [];
  const bonusTier = issueAgeBonusLabel(bonuses);
  const bandLabel = premiumBandLabel(match.accounts);

  const bestCommission = commissions.reduce(
    (best: number, c: { commissionPct: number | null }) =>
      c.commissionPct != null && Number(c.commissionPct) > best ? Number(c.commissionPct) : best,
    0
  );
  const bestBonus = bestPremiumBonus(bonuses)?.theRate ?? 0;

  // Show commission bands if available
  const commissionBands = commissions
    .filter((c) => c.commissionPct != null && Number(c.commissionPct) > 0)
    .sort((a, b) => (a.fromAge ?? 0) - (b.fromAge ?? 0));

  return (
    <div>
      <DetailLabel>Commission & Bonus</DetailLabel>
      <div className="space-y-1 text-xs">
        {bestBonus > 0 ? (
          <div className="flex justify-between gap-2">
            <span className="text-muted-foreground">Premium bonus</span>
            <span className="text-right font-semibold text-success">
              {bestBonus}%
              {bonusTier && (
                <span className="block text-[10px] font-normal text-muted-foreground">
                  {bonusTier}
                </span>
              )}
            </span>
          </div>
        ) : (
          <div className="flex justify-between">
            <span className="text-muted-foreground">Premium bonus</span>
            <span className="text-muted-foreground">None</span>
          </div>
        )}
        {bandLabel && (
          <div className="flex justify-between gap-2">
            <span className="text-muted-foreground">Premium band</span>
            <span className="text-right font-medium">{bandLabel}</span>
          </div>
        )}
        {bestCommission > 0 && (
          <div className="flex justify-between">
            <span className="text-muted-foreground">Best commission</span>
            <span className="font-semibold">{bestCommission}%</span>
          </div>
        )}
        {commissionBands.length > 1 && (
          <div className="mt-1 pt-1 border-t border-border/30">
            <span className="text-[10px] text-muted-foreground">Commission by age:</span>
            {commissionBands.map((c, i) => (
              <div key={i} className="flex justify-between">
                <span className="text-muted-foreground">
                  {c.fromAge != null && c.toAge != null
                    ? `Ages ${c.fromAge}–${c.toAge}`
                    : c.fromAge != null
                      ? `Age ${c.fromAge}+`
                      : 'All ages'}
                </span>
                <span className="font-medium">{c.commissionPct}%</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function RiderList({ riders }: { riders: ProductRider[] }) {
  if (!riders || riders.length === 0) return null;
  return (
    <div>
      <DetailLabel>Income Riders</DetailLabel>
      <div className="space-y-2">
        {riders.map((r: ProductRider, i: number) => (
          <div key={i} className="text-xs space-y-0.5">
            <div className="flex items-center justify-between">
              <TruncatedTooltip text={r.riderName} className="mr-2 font-medium" />
              <span className="text-muted-foreground shrink-0">
                {r.feeRateText ? `Fee: ${r.feeRateText}` : '—'}
              </span>
            </div>
            {r.withdrawalBenefitType && (
              <div className="text-muted-foreground">Type: {r.withdrawalBenefitType}</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function IncomeRiderDetail({ match }: { match: ProductComparisonMatch }) {
  const quote = match.incomeRiderQuote;
  if (!quote) return null;

  return (
    <div>
      <DetailLabel>Income Rider Details</DetailLabel>
      <div className="space-y-1.5 text-xs">
        <div className="flex justify-between">
          <span className="text-muted-foreground">Annual Income</span>
          <span className="font-semibold">${quote.incomeAmount.toLocaleString()}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-muted-foreground">Payout Rate</span>
          <span className="font-medium">
            {quote.benefitRate}% at age {quote.benefitAge}
          </span>
        </div>

        {quote.rollUpRate != null && (
          <div className="flex justify-between">
            <span className="text-muted-foreground">Roll-up</span>
            <span className="font-medium">
              {quote.rollUpRate}% {quote.rollUpType ?? ''}
            </span>
          </div>
        )}

        <div className="flex justify-between">
          <span className="text-muted-foreground">Rider Fee</span>
          <span className="font-medium">{quote.feeRate}%</span>
        </div>

        {/* Joint payout comparison */}
        {quote.jointIncomeAmount != null && (
          <div className="mt-1 pt-1 border-t border-border/30 space-y-0.5">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Joint Income</span>
              <span className="font-medium">${quote.jointIncomeAmount.toLocaleString()}/yr</span>
            </div>
            {quote.jointBenefitRate != null && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Joint Payout Rate</span>
                <span className="font-medium">{quote.jointBenefitRate}%</span>
              </div>
            )}
            {quote.jointEnhancementMultiplier != null && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Joint LTC Multiplier</span>
                <span className="font-medium">{quote.jointEnhancementMultiplier}x</span>
              </div>
            )}
          </div>
        )}

        {quote.hasLTCEnhancement && (
          <div className="mt-1 pt-1 border-t border-border/30 space-y-0.5">
            <div className="flex justify-between">
              <span className="text-muted-foreground">LTC Enhancement</span>
              <span className="font-medium text-success">
                {quote.enhancementMultiplier ? `${quote.enhancementMultiplier}x` : 'Yes'}
              </span>
            </div>
            {quote.enhancedIncomeAmount != null && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Enhanced Income</span>
                <span className="font-medium">
                  ${quote.enhancedIncomeAmount.toLocaleString()}/yr
                </span>
              </div>
            )}
            {quote.ltcQualifierType && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Qualifier</span>
                <span className="font-medium">{quote.ltcQualifierType}</span>
              </div>
            )}
            {quote.ltcWaitingPeriodYears != null && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Waiting Period</span>
                <span className="font-medium">
                  {quote.ltcWaitingPeriodYears} year{quote.ltcWaitingPeriodYears !== 1 ? 's' : ''}
                </span>
              </div>
            )}
          </div>
        )}

        <div className="mt-1 pt-1 border-t border-border/30 space-y-0.5">
          <div className="flex justify-between">
            <span className="text-muted-foreground">10-Year Total</span>
            <span className="font-medium">${quote.income10YearTotal.toLocaleString()}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Lifetime (to 95)</span>
            <span className="font-medium">${quote.incomeAge95Total.toLocaleString()}</span>
          </div>
        </div>

        {quote.alternativeStarts.length > 0 && (
          <div className="mt-1 pt-1 border-t border-border/30">
            <span className="text-[10px] text-muted-foreground">Payout by start age:</span>
            <div className="grid grid-cols-3 gap-1 mt-0.5">
              {quote.alternativeStarts.map((alt, i) => (
                <div key={i} className="text-center rounded bg-muted/30 px-1 py-0.5">
                  <div className="font-medium">
                    {alt.benefitRate != null
                      ? `${alt.benefitRate}%`
                      : `$${alt.incomeAmount.toLocaleString()}`}
                  </div>
                  <div className="text-[9px] text-muted-foreground">Age {alt.benefitAge}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function SurrenderScheduleDisplay({ match }: { match: ProductComparisonMatch }) {
  const schedules = match.surrenderSchedules;
  if (!schedules || schedules.length === 0) return null;
  const schedule = schedules[0];
  const rates = schedule.ratesByYear;
  if (!rates || rates.length === 0) return null;

  return (
    <div>
      <DetailLabel>Surrender Schedule</DetailLabel>
      <div className="flex gap-1">
        {rates.map((entry, i: number) => {
          const r = typeof entry === 'number' ? entry : entry.rate;
          const yr = typeof entry === 'number' ? i + 1 : entry.year;
          return (
            <div key={i} className="text-center">
              <div
                className={cn(
                  'text-[10px] font-medium rounded px-1 py-0.5',
                  r > 5
                    ? 'bg-destructive/10 text-destructive'
                    : r > 0
                      ? 'bg-warning/10 text-warning'
                      : 'bg-success/10 text-success'
                )}
              >
                {r}%
              </div>
              <div className="text-[9px] text-muted-foreground mt-0.5">Y{yr}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Per-product AI diff summary (lazy loaded on expand)
// ---------------------------------------------------------------------------

function ProductDiffSummary({
  opportunityId,
  productId,
}: {
  opportunityId: string;
  productId: string;
}) {
  const { data: diff, isLoading, error } = useProductDiff(opportunityId, productId);

  if (error) return null;

  if (isLoading) {
    return (
      <div className="mt-4 pt-3 border-t border-info/15">
        <div className="flex items-center gap-2 mb-2">
          <Skeleton className="h-3 w-3 bg-info/15" />
          <Skeleton className="h-3 w-24 bg-info/15" />
        </div>
        <Skeleton className="h-3 w-full bg-info/15" />
        <Skeleton className="h-3 w-[85%] mt-1 bg-info/15" />
      </div>
    );
  }

  if (!diff) return null;

  return (
    <div className="mt-4 pt-3 border-t border-border/50">
      <div className="flex items-center gap-1.5 mb-2">
        <Sparkles className="h-3 w-3 text-info" />
        <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
          AI Analysis
        </span>
      </div>
      <p className="text-xs leading-relaxed text-foreground">{diff}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// MatchRow — the row expands in-place with a highlighted detail card
// ---------------------------------------------------------------------------

function MatchRow({
  match,
  isBest,
  opportunityId,
  selectable,
  isChecked,
  onToggle,
  onDismiss,
}: {
  match: ProductComparisonMatch;
  isBest: boolean;
  opportunityId: string;
  selectable?: boolean;
  isChecked?: boolean;
  onToggle?: () => void;
  onDismiss?: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const strategies = match.rateComparison?.strategyComparisons ?? [];
  const riders = match.riders ?? [];
  const commissions = match.commissions ?? [];
  const bonuses = match.premiumBonuses ?? [];
  const hasDetails =
    strategies.length > 0 ||
    riders.length > 0 ||
    commissions.length > 0 ||
    bonuses.length > 0 ||
    match.mva ||
    match.rop ||
    match.withdrawalAllowed ||
    (match.surrenderSchedules?.length ?? 0) > 0;

  return (
    <>
      <tr
        className={cn(
          'table-row-striped',
          'hover:bg-muted/30 transition-colors',
          expanded && 'bg-muted/20'
        )}
      >
        {selectable && (
          <td className="sticky left-0 z-10 bg-surface-0 w-12 px-3 py-3 text-center border-r border-border">
            <input
              type="checkbox"
              checked={Boolean(isChecked)}
              onChange={() => onToggle?.()}
              aria-label={`Select ${match.carrierName}`}
              className="h-4 w-4 rounded border-border accent-primary"
            />
          </td>
        )}
        <td
          className={cn(
            'whitespace-nowrap px-4 py-3 text-sm font-medium',
            hasDetails && 'cursor-pointer'
          )}
          onClick={() => hasDetails && setExpanded((prev) => !prev)}
        >
          <div className="flex items-center gap-1.5">
            {hasDetails ? (
              <span className="text-muted-foreground">
                {expanded ? (
                  <ChevronDown className="h-3.5 w-3.5" />
                ) : (
                  <ChevronRight className="h-3.5 w-3.5" />
                )}
              </span>
            ) : (
              <span className="w-3.5" />
            )}
            {match.carrierName}
            {isBest && <span className="ml-1 text-[10px] font-normal text-primary">(Best)</span>}
          </div>
        </td>
        {/* Other columns — click to expand/collapse */}
        {COLUMNS.map((col) => (
          <td
            key={col}
            className={cn('whitespace-nowrap px-4 py-3 text-sm', hasDetails && 'cursor-pointer')}
            onClick={() => hasDetails && setExpanded((prev) => !prev)}
          >
            {getMatchValue(col, match)}
          </td>
        ))}
        {onDismiss && (
          <td className="whitespace-nowrap px-4 py-3 text-right text-sm">
            <button
              type="button"
              className="text-xs font-medium text-muted-foreground/60 transition-colors hover:text-muted-foreground"
              onClick={(event) => {
                event.stopPropagation();
                onDismiss();
              }}
            >
              Dismiss
            </button>
          </td>
        )}
      </tr>

      {expanded && (
        <tr style={{ borderTopWidth: 0 }}>
          {selectable && (
            <td className="sticky left-0 z-10 bg-surface-0 border-r border-border p-0" />
          )}
          <td colSpan={COLUMNS.length + (onDismiss ? 2 : 1)} className="p-0">
            <div className="mx-4 my-3 rounded-lg border border-info/25 bg-detail-surface p-4 shadow-inset">
              <div className="grid gap-8" style={{ gridTemplateColumns: '1.2fr 1fr 1fr' }}>
                <div className="pr-6">
                  <StrategyList strategies={strategies} />
                </div>
                <div className="space-y-4">
                  <FeatureBadges match={match} />
                  <RiderList riders={riders} />
                  <SurrenderScheduleDisplay match={match} />
                </div>
                <div className="space-y-4">
                  <CommissionBonusSection match={match} />
                  <IncomeRiderDetail match={match} />
                </div>
              </div>
              <ProductDiffSummary opportunityId={opportunityId} productId={match.productId} />
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// ComparisonTable
// ---------------------------------------------------------------------------

export function ComparisonTable({
  opportunityId,
  currentPolicy,
  matches,
  selectable = false,
  checkedIds,
  onToggleCheck,
  onDismissProduct,
}: ComparisonTableProps) {
  const sorted = matches;
  const bestId = [...matches].sort((a, b) => b.recommendationScore - a.recommendationScore)[0]
    ?.productId;

  return (
    <div className="overflow-x-auto rounded-lg border">
      <table className="min-w-full divide-y divide-border">
        <thead className="bg-surface-0">
          <tr>
            {selectable && (
              <th className="sticky left-0 z-10 bg-surface-0 w-12 px-3 py-3 text-center text-xs font-medium uppercase tracking-wider text-muted-foreground border-r border-border">
                <span className="sr-only">Select</span>
              </th>
            )}
            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Carrier
            </th>
            {COLUMNS.map((col) => (
              <th
                key={col}
                className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground"
              >
                {col}
              </th>
            ))}
            {onDismissProduct && (
              <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Action
              </th>
            )}
          </tr>
        </thead>
        <tbody className="divide-y divide-border bg-card">
          {/* Current policy row */}
          <tr className="table-row-striped">
            {selectable && (
              <td className="sticky left-0 z-10 bg-surface-0 w-12 px-3 py-3 border-r border-border" />
            )}
            <td className="whitespace-nowrap px-4 py-3 text-sm font-medium">
              <div className="flex items-center gap-1.5">
                <span className="w-3.5" />
                {currentPolicy.carrierName}
                <span className="text-xs font-normal text-muted-foreground">(Current)</span>
              </div>
            </td>
            {COLUMNS.map((col) => (
              <td key={col} className="whitespace-nowrap px-4 py-3 text-sm text-muted-foreground">
                {getCurrentValue(col, currentPolicy)}
              </td>
            ))}
            {onDismissProduct && <td className="px-4 py-3" />}
          </tr>

          {/* Match rows */}
          {sorted.map((m) => (
            <RowErrorBoundary key={m.productId}>
              <MatchRow
                match={m}
                isBest={m.productId === bestId}
                opportunityId={opportunityId}
                selectable={selectable}
                isChecked={checkedIds?.has(m.productId)}
                onToggle={() => onToggleCheck?.(m)}
                onDismiss={onDismissProduct ? () => onDismissProduct(m) : undefined}
              />
            </RowErrorBoundary>
          ))}
        </tbody>
      </table>
    </div>
  );
}
